/* oxlint-disable unicorn/prefer-set-has -- LINTER migration follow up */
import { useIsInternalUserOrImpersonated } from "@grafit/frontend-auth/use-auth";
import { setFeatureFlagsContext } from "@grafit/shared/observability/sentry-front";
import { isLocal } from "@grafit/shared/utils/envs.front";
import { useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface Category {
  id: "dev-tools" | "grafiters" | "beta-features";
  name: string;
  color: string;
  icon: React.ReactNode;
  index: number;
}

interface FeatureFlagConfigEntry {
  name: string;
  description: string;
  defaultEnabled: boolean | undefined | (() => boolean);
  category: Category["id"];
}

const FEATURE_FLAGS_CONFIG = {
  "tanstack-devtools": {
    name: "Tanstack Devtools",
    description: "Enable the new TanStack devtools (react-router + react-query)",
    defaultEnabled: isLocal,
    category: "dev-tools",
  },
  import_catalog: {
    name: "Import catalog",
    description: "Allows you to import a catalog of pre-filled bidrows (with tasks and purchases)",
    defaultEnabled: undefined, // initial value will be "isInternalUserOrImpersonated", until overridden by the admin page
    category: "grafiters",
  },
  "sound-effects": {
    name: "Sound effects",
    description: "Enable sound effects (when creating a new bidrow)",
    defaultEnabled: false,
    category: "grafiters",
  },
  "import-supplier-quote-mock-ai": {
    name: "Import Supplier Quote: Mock AI",
    description: "Use mock data instead of the actual AI extraction call for supplier quote import",
    defaultEnabled: false,
    category: "dev-tools",
  },
  "split-bidrow-v3": {
    name: "Split Bidrow V3",
    description: "Enable split bidrow v3 to infer equipments and tasks from bidrow data",
    defaultEnabled: false,
    category: "beta-features",
  },
  "sqr-mails": {
    name: "Consultations fournisseurs par mail",
    description: "Permet d'envoyer une consultation fournisseur par mail",
    defaultEnabled: false,
    category: "beta-features",
  },
  "client-pricing-document-fill": {
    name: "Bordereau client",
    description: "Permet d'associer le bordereau Excel du client à un marché et d'y exporter les devis",
    defaultEnabled: undefined,
    category: "beta-features",
  },
  "indirect-costs-hours-popover": {
    name: "Coûts indirects : heures non affectées",
    description:
      "Sépare les heures des prestations non affectées des heures bordereau et édite les heures hors bordereau dans une popover",
    defaultEnabled: false,
    category: "beta-features",
  },
} as const satisfies Record<string, FeatureFlagConfigEntry>;

export type FeatureFlagId = keyof typeof FEATURE_FLAGS_CONFIG;

export interface FeatureFlag {
  id: FeatureFlagId;
  name: string;
  description: string;
  enabled: boolean | undefined;
  category: Category["id"];
}

interface FeatureFlagStore {
  featureFlags: FeatureFlag[];
  toggleFlag: (featureFlagId: FeatureFlagId, value: boolean) => void;
  reset: (flagsToAdd: FeatureFlag[], flagsToRemove: FeatureFlagId[]) => void;
}

const getInitialFeatureFlags = (): FeatureFlag[] => {
  return Object.entries(FEATURE_FLAGS_CONFIG).map(([id, config]) => ({
    id: id as FeatureFlagId,
    name: config.name,
    description: config.description,
    enabled: typeof config.defaultEnabled === "function" ? config.defaultEnabled() : config.defaultEnabled,
    category: config.category,
  }));
};

export const useFeatureFlagStore = create<FeatureFlagStore>()(
  persist(
    (set) => ({
      featureFlags: getInitialFeatureFlags(),
      toggleFlag: (featureFlagId: FeatureFlagId, value: boolean) => {
        set((prevState) => ({
          featureFlags: prevState.featureFlags.map((flag) =>
            flag.id === featureFlagId ? { ...flag, enabled: value } : flag,
          ),
        }));
      },
      reset: (flagsToAdd: FeatureFlag[], flagsToRemove: FeatureFlagId[]) => {
        set((prevState) => ({
          featureFlags: prevState.featureFlags.filter((flag) => !flagsToRemove.includes(flag.id)).concat(flagsToAdd),
        }));
      },
    }),
    {
      name: "feature-flags",
      partialize: (state) => ({ featureFlags: state.featureFlags }), // avoid persisting the toggleFlag function
      onRehydrateStorage: () => (state) => {
        if (!state) {
          return;
        }

        const persistedFlagIds = state.featureFlags.map((f) => f.id);
        const initialFeatureFlags = getInitialFeatureFlags();
        const initialFlagIds = initialFeatureFlags.map((f) => f.id);

        const missingFlags = initialFeatureFlags.filter((flag) => !persistedFlagIds.includes(flag.id));
        const flagsToRemove = persistedFlagIds.filter((flagId) => !initialFlagIds.includes(flagId));

        state.reset(missingFlags, flagsToRemove);
      },
    },
  ),
);

export const useReportFeatureFlagsContext = () => {
  const featureFlags = useFeatureFlagStore((state) => state.featureFlags);
  const isInternalUserOrImpersonated = useIsInternalUserOrImpersonated();

  useEffect(() => {
    setFeatureFlagsContext(
      featureFlags.map((featureFlag) => ({
        id: featureFlag.id,
        enabled: getFeatureFlagValue(featureFlag, isInternalUserOrImpersonated),
      })),
    );
  }, [featureFlags, isInternalUserOrImpersonated]);
};

export const useFeatureFlag = (featureFlagId: FeatureFlagId): boolean => {
  const isInternalUserOrImpersonated = useIsInternalUserOrImpersonated();
  const featureFlags = useFeatureFlagStore((state) => state.featureFlags);

  const flag = featureFlags.find((f) => f.id === featureFlagId);

  if (!flag) {
    return false;
  }

  return getFeatureFlagValue(flag, isInternalUserOrImpersonated);
};

export const getFeatureFlagValue = (flag: FeatureFlag, isInternalUserOrImpersonated: boolean) => {
  if (flag.enabled === undefined) {
    return isInternalUserOrImpersonated;
  }

  return flag.enabled;
};
